import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X } from 'lucide-react';
import { NavHamburger, MobileDrawer } from './AdminHeader.styles';

const EDGE = 14; // px — the drawer keeps this far from the screen's sides and bottom
const DRAWER_WIDTH = 280;

/**
 * NavMenu - a hamburger and its drawer of links for the admin header's narrow
 * layouts: Ylläpito's pages in the top bar, the open stage's pages in the
 * stage row. The drawer is portalled and fixed under the button, because the
 * stage row blurs and scrolls sideways (which would clip it) and the header's
 * rows stack over each other. It scrolls when taller than the room below the
 * button, and closes on a click inside it or outside it.
 */
const NavMenu = ({ label, align = 'left', compact = false, children }) => {
  // The button's position while open, null while closed.
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const drawerRef = useRef(null);
  const open = anchor !== null;

  useEffect(() => {
    if (!open) return undefined;
    const inside = (ref, e) => ref.current && ref.current.contains(e.target);
    const close = (e) => { if (!inside(buttonRef, e) && !inside(drawerRef, e)) setAnchor(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => setAnchor(open ? null : buttonRef.current.getBoundingClientRect());

  const drawerStyle = () => {
    const top = anchor.bottom + 8;
    const side = align === 'right'
      ? { right: Math.max(EDGE, window.innerWidth - anchor.right) }
      : { left: Math.max(EDGE, Math.min(anchor.left, window.innerWidth - DRAWER_WIDTH - EDGE)) };
    return { top, maxHeight: `calc(100dvh - ${top + EDGE}px)`, ...side };
  };

  return (
    <>
      <NavHamburger
        ref={buttonRef}
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        $compact={compact}
        onClick={toggle}
      >
        {open ? <X size={compact ? 18 : 20} /> : <Menu size={compact ? 18 : 20} />}
      </NavHamburger>
      {open && createPortal(
        <MobileDrawer ref={drawerRef} aria-label={label} style={drawerStyle()} onClick={() => setAnchor(null)}>
          {children}
        </MobileDrawer>,
        document.body,
      )}
    </>
  );
};

export default NavMenu;
